import { DataFile } from 'client/store/files/filesReducer'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { SeoInterface, Tracking } from 'common/types/Page'
import { websiteRequest } from 'src/editor/shared/utils/request'
import {
  GetWebsitePagesResponse,
  WebsitePageInterface,
} from 'src/editor/store/website-pages/website-pages-types'
import { WebsitePageResource } from 'src/shared/types/website-page-interface'
import { WebsitePageSavePayloadInterface } from 'src/shared/types/website-page-save-payload-interface'
import { pageEditorHttpClient } from '../shared/utils/page-editor-http-client'

export const getWebsitePageById = (id: number) =>
  websiteRequest<WebsitePageInterface>(`website-pages/${id}`, 'GET')

export const getWebsitePages = async (websiteId: number) =>
  websiteRequest<GetWebsitePagesResponse>(`websites/${websiteId}/pages`, 'GET')

export interface GetWebsiteTrackingAndSeoResponse {
  id: number
  tracking: Tracking
  seo: SeoSettingsFields
}

export const getWebsiteTrackingAndSeo = async (websiteId: number) =>
  pageEditorHttpClient<GetWebsiteTrackingAndSeoResponse>(
    `page/${websiteId}/seo-and-tracking`,
    'GET',
  )

export interface UpdateGeneralWebsitePageRequest {
  websitePageId: number
  name: string
  url: string
}

export const updateWebsitePageRequest = async ({
  websitePageId,
  name,
  url,
}: UpdateGeneralWebsitePageRequest) =>
  websiteRequest<void>(`website-pages/${websitePageId}`, 'PUT', {
    data: {
      path: url,
      name,
    },
  })

export interface SeoSettingsFields extends Omit<SeoInterface, 'socialImage'> {
  title: string
  description: string
  author: string
  keywords: string
  index?: boolean
  socialImage?: DataFile
}

export interface UpdateSeoWebsitePageRequest
  extends Omit<SeoSettingsFields, 'socialImage'> {
  socialImage?: number
}

export const updateWebsitePageSeo = async ({
  pageContentId,
  ...rest
}: UpdateSeoWebsitePageRequest & { pageContentId: number }) =>
  pageEditorHttpClient<UpdateSeoWebsitePageRequest>(
    `page/${pageContentId}/seo`,
    'PUT',
    {
      data: rest,
    },
  )

export interface UpdateAdvancedWebsitePageRequest extends Tracking {
  pageId: number
}

export const updateWebsitePageAdvanced = async ({
  pageId,
  ...rest
}: UpdateAdvancedWebsitePageRequest) =>
  pageEditorHttpClient<Tracking>(`page/${pageId}/tracking`, 'PUT', {
    data: rest,
  })

export interface CreateWebsitePageRequest {
  websiteId: number
  pageType: string
  name: string
  url: string
  locale: PageLocaleEnum
}

export const createWebsitePage = async ({
  websiteId,
  pageType,
  name,
  url,
  locale,
}: CreateWebsitePageRequest) =>
  websiteRequest<WebsitePageResource>(`websites/${websiteId}/pages`, 'POST', {
    data: {
      type: pageType,
      path: url,
      name,
      locale,
    },
  })

export interface PublishWebsitePageRequest {
  websitePageId: number
}

export const publishWebsitePage = async ({
  websitePageId,
}: PublishWebsitePageRequest) =>
  websiteRequest<WebsitePageResource>(
    `website-pages/${websitePageId}/activate`,
    'POST',
    { data: {} },
  )

export interface UnpublishWebsitePageRequest {
  websitePageId: number
}

export const unpublishWebsitePage = async ({
  websitePageId,
}: UnpublishWebsitePageRequest) =>
  websiteRequest<WebsitePageResource>(
    `website-pages/${websitePageId}/deactivate`,
    'POST',
    { data: {} },
  )

export interface DeleteWebsitePageRequest {
  websitePageSetId: number
}

export const deleteWebsitePageRequest = async ({
  websitePageSetId,
}: DeleteWebsitePageRequest) =>
  websiteRequest<void>(`website-pages/${websitePageSetId}`, 'DELETE')

export interface AddWebsitePageLocaleRequest {
  websitePageSetId: number
  locale: PageLocaleEnum
}

export const addWebsitePageLocale = async ({
  websitePageSetId,
  locale,
}: AddWebsitePageLocaleRequest) =>
  websiteRequest<void>(`website-pages/${websitePageSetId}/add-locale`, 'POST', {
    data: {
      locale,
    },
  })

export interface RemoveWebsitePageLocaleRequest {
  websitePageSetId: number
  locale: PageLocaleEnum
}

export const removeWebsitePageLocale = async ({
  websitePageSetId,
  locale,
}: RemoveWebsitePageLocaleRequest) =>
  websiteRequest<void>(
    `website-pages/${websitePageSetId}/remove-locale`,
    'POST',
    {
      data: {
        locale,
      },
    },
  )

export interface CopyPageLocaleRequest {
  websitePageSetId: number
  locale: PageLocaleEnum
}

export const copyPageLocale = async ({
  websitePageSetId,
  locale,
}: CopyPageLocaleRequest) =>
  websiteRequest<void>(`website-pages/${websitePageSetId}/copy-page`, 'POST', {
    data: {
      locale,
    },
  })

export const saveWebsitePage = async (
  payload: WebsitePageSavePayloadInterface,
) =>
  pageEditorHttpClient(`page/${payload.contentPage.id}/save`, 'put', {
    data: payload,
  })
